import { c as _c } from "react/compiler-runtime";
// Secrets (Plan secrets s4; Decisions sealed-store-protocol, secrets-model): which secrets exist per project (by
// profile: rev, changed, reads, last reader, stale), who can decrypt (recipients), and a write-only form that encrypts
// a new value in the browser. A value is shown only on this device's key (hq:Decision/device-keys, ./device.ts): after
// a passkey confirm, for SHOWN_MS, each read a read event; the recipients list says which keys still wait for a reseal
// and revokes my devices. Contract with the shell (layouts/root.tsx):
//   - <SecretsList project onChange/>: the Secrets view's page body (/secrets). `project` = the scope picker's
//     ?project=: that project with its recipients; null (All) = every project I may see, grouped by project.
//   - <AddSecret value onClose/>: the Add panel (?secret=new | <project> | <project>|<profile>|<key> to change one):
//     target project pills, name, profile, value. A booklet page on desktop, the one Sheet on phones.
//   - canUseSecrets / canWriteSecrets(session[, project]).
// A save or remove emits a live `truss.Secret` (live/bus), which reloads every list on screen.
// Shared pieces only (Segmented, SectionHead, Group/Stack/Item, Field, LinkButton, ErrorLine, Loader, .note).
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type SecretsData, type Session } from "@/api";
import { ErrorLine } from "@/components/error-line";
import { Field } from "@/components/field";
import { Item, Group as List } from "@/components/group";
import { LinkButton } from "@/components/link-button";
import { Loader } from "@/components/loader";
import { Icon } from "@/components/icon";
import { IconButton } from "@/components/icon-button";
import { Row } from "@/components/row";
import { SectionHead } from "@/components/section-head";
import { Segment, Segmented } from "@/components/segmented";
import { useSession } from "@/components/settings";
import { expire, useLoad } from "@/lib/load";
import { emitLive } from "@/live/bus";
import { useData } from "@/store";
import { type Ask, forget, local, reseal, reveal, setUp } from "./device";
import { registerSecretsStrings } from "./locale";
import { groups, invalid, shortKey } from "./model";

// Secret permissions come from space memberships (truss:Interface/secrets): per space, never global.
const USE = ["truss.Secret.read", "truss.Secret.manage"];
const grants = (s: Session | null, project: string | null, ps: string[]) => !!s && (s.projects ?? []).some(p => (project === null || p.project.id === project) && ps.some(x => p.permissions.includes(x)));
export const canUseSecrets = (s: Session | null) => grants(s, null, USE);
export const canWriteSecrets = (s: Session | null, project: string | null) => !!project && grants(s, project, ["truss.Secret.manage"]);
const date = (iso: string) => new Date(iso).toLocaleDateString([], {
  day: "numeric",
  month: "short"
});
const KINDS = ["Secret"];
/** How long a revealed value stays on screen (also hidden when the page is hidden). */
const SHOWN_MS = 30_000;
/** After a write: every secrets list on screen loads again (not from the shared answer of the last 2 s). */
const changed = (id: string) => (expire("secrets:"), emitLive("truss.Secret", id));
const deviceName = () => /iPhone|iPad|Android|Mac|Windows|Linux/.exec(navigator.userAgent)?.[0] ?? "browser";

/** A call with its busy flag and error line. */
function useTask() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await f();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return {
    busy,
    error,
    run
  };
}

/** The passphrase fallback (no PRF): `ask` shows a hidden field in place and resolves with what is typed. */
function usePassphrase() {
  const $ = _c(11);
  const {
    t
  } = useTranslation();
  const [pending, setPending] = useState(null);
  const [v, setV] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = fresh => new Promise(done => setPending({
      fresh,
      done
    }));
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const ask = t0;
  let t1;
  if ($[1] !== pending || $[2] !== v) {
    t1 = () => {
      pending?.done(v);
      setPending(null);
      setV("");
    };
    $[1] = pending;
    $[2] = v;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const finish = t1;
  let t2;
  if ($[4] !== finish || $[5] !== pending || $[6] !== t || $[7] !== v) {
    t2 = pending && <Field t="device-passphrase" type="password" autoComplete="off" placeholder={t(pending.fresh ? "secrets.newPassphrase" : "secrets.passphrase")} aria-label={t("secrets.passphrase")} value={v} onChange={e => setV(e.target.value)} onKeyDown={e_0 => e_0.key === "Enter" && v && finish()} action={<LinkButton disabled={!v} onClick={finish}>{t("secrets.unlock")}</LinkButton>} />;
    $[4] = finish;
    $[5] = pending;
    $[6] = t;
    $[7] = v;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const field = t2;
  let t3;
  if ($[9] !== field) {
    t3 = {
      ask,
      field
    };
    $[9] = field;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

/** This device's key: set it up (a passkey, or a passphrase), or its public key to compare with the recipients. */
function ThisDevice() {
  const $ = _c(27);
  const {
    t
  } = useTranslation();
  const person = useSession()?.person;
  const {
    ask,
    field
  } = usePassphrase();
  const task = useTask();
  let T0;
  let k;
  let t0;
  let t1;
  let t2;
  if ($[0] !== ask || $[1] !== person || $[2] !== t || $[3] !== task) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      k = person ? local(person.id) : null;
      if (!person) {
        t2 = null;
        break bb0;
      }
      let t3;
      if ($[9] !== ask || $[10] !== person || $[11] !== task) {
        t3 = () => task.run(() => setUp(person, deviceName(), ask)).then(_temp);
        $[9] = ask;
        $[10] = person;
        $[11] = task;
        $[12] = t3;
      } else {
        t3 = $[12];
      }
      const set = t3;
      t1 = "this-device";
      T0 = List;
      t0 = k ? <Item t="device-key" label={t("secrets.thisDevice")} value={shortKey(k.public)} mono={true} /> : <li><Row t="device-setup" label={task.busy ? t("secrets.settingUp") : t("secrets.setUp")} chevron={true} disabled={task.busy} onClick={() => void set()} /></li>;
    }
    $[0] = ask;
    $[1] = person;
    $[2] = t;
    $[3] = task;
    $[4] = T0;
    $[5] = k;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    T0 = $[4];
    k = $[5];
    t0 = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[13] !== T0 || $[14] !== t0) {
    t3 = <T0>{t0}</T0>;
    $[13] = T0;
    $[14] = t0;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== task.error) {
    t4 = task.error && <ErrorLine>{task.error}</ErrorLine>;
    $[16] = task.error;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== k || $[19] !== t) {
    t5 = !k && <p className="note">{t("secrets.setUpNote")}</p>;
    $[18] = k;
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== field || $[22] !== t1 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t6 = <section data-t={t1}>{t3}{field}{t4}{t5}</section>;
    $[21] = field;
    $[22] = t1;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}

/** Projects whose secrets the caller may see: this home's by its session's grants (all until it's known), and every
 *  connected instance's (read-only: what the home's grant there allows). `write`: this home's writable ones only. */
function _temp(ok) {
  return ok && changed("device");
}
function useSecretProjects(t0) {
  const $ = _c(12);
  const write = t0 === undefined ? false : t0;
  const all = useData(_temp2);
  const session = useSession();
  let t1;
  if ($[0] !== all) {
    t1 = all ?? [];
    $[0] = all;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== session || $[3] !== t1 || $[4] !== write) {
    let t3;
    if ($[6] !== session || $[7] !== write) {
      t3 = p => p.status !== "archived" && (p.instance ? !write : !session || grants(session, p.id, write ? ["truss.Secret.manage"] : USE));
      $[6] = session;
      $[7] = write;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = t1.filter(t3);
    $[2] = session;
    $[3] = t1;
    $[4] = write;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const ps = t2;
  let t3;
  if ($[9] !== ps || $[10] !== session) {
    t3 = {
      projects: ps,
      session
    };
    $[9] = ps;
    $[10] = session;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function _temp2(s) {
  return s.snapshot?.projects;
}
type Ref = (project: string, profile: string, key: string) => void;

/** The Secrets view's body: one project (with recipients) or every project I may see. Rows: the name, a stale mark,
 *  Change / Remove icons; a tap on the name opens the details panel (`onOpen`). */
export function SecretsList(t0) {
  const $ = _c(22);
  const {
    project,
    onChange,
    onOpen
  } = t0;
  registerSecretsStrings();
  const {
    t
  } = useTranslation();
  const {
    projects,
    session
  } = useSecretProjects();
  let t1;
  if ($[0] !== project || $[1] !== projects) {
    t1 = project ? projects.filter(p => p.id === project) : projects;
    $[0] = project;
    $[1] = projects;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shown = t1;
  if (!shown.length) {
    let t2;
    if ($[3] !== t) {
      t2 = t("secrets.noProjects");
      $[3] = t;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t2) {
      t3 = <p className="note">{t2}</p>;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ThisDevice />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== onChange || $[9] !== onOpen || $[10] !== project || $[11] !== session || $[12] !== shown) {
    let t4;
    if ($[14] !== onChange || $[15] !== onOpen || $[16] !== project || $[17] !== session || $[18] !== shown.length) {
      t4 = p_0 => <ProjectSecrets key={p_0.id} project={p_0.id} name={shown.length > 1 ? p_0.instance ? `${p_0.name} @${p_0.instance}` : p_0.name : null} canWrite={canWriteSecrets(session, p_0.id)} recipients={!!project} onChange={onChange} onOpen={onOpen} />;
      $[14] = onChange;
      $[15] = onOpen;
      $[16] = project;
      $[17] = session;
      $[18] = shown.length;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    t3 = shown.map(t4);
    $[8] = onChange;
    $[9] = onOpen;
    $[10] = project;
    $[11] = session;
    $[12] = shown;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[20] !== t3) {
    t4 = <div data-t="secrets">{t2}{t3}</div>;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
}
function ProjectSecrets(t0) {
  const $ = _c(38);
  const {
    project,
    name,
    canWrite,
    recipients,
    onChange,
    onOpen
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== project) {
    t1 = () => api.secrets.list(project);
    $[0] = project;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      kinds: KINDS
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const data = useLoad(t1, `secrets:${project}`, t2);
  const {
    confirm,
    ask,
    remove,
    error
  } = useRemove(project);
  let t3;
  if ($[3] !== ask || $[4] !== canWrite || $[5] !== confirm || $[6] !== data.data || $[7] !== data.error || $[8] !== data.loading || $[9] !== data.reload || $[10] !== error || $[11] !== name || $[12] !== onChange || $[13] !== onOpen || $[14] !== project || $[15] !== recipients || $[16] !== remove || $[17] !== t) {
    const gs = data.data ? groups(data.data) : [];
    let t4;
    if ($[19] !== name) {
      t4 = profile => name ? `${name} \u00b7 ${profile}` : profile;
      $[19] = name;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    const head = t4;
    let t5;
    if ($[21] !== data.loading) {
      t5 = <Loader on={data.loading} />;
      $[21] = data.loading;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    let t6;
    if ($[23] !== ask || $[24] !== canWrite || $[25] !== confirm || $[26] !== head || $[27] !== onChange || $[28] !== onOpen || $[29] !== project || $[30] !== remove || $[31] !== t) {
      t6 = g => <section key={g.profile} data-t="secret-profile" data-profile={g.profile}><SectionHead>{head(g.profile)}</SectionHead><List>{g.rows.map(r => {
            const ref = `${r.profile}/${r.key}`;
            return <li key={r.id} className="secret-row" data-t="secret" data-ref={ref} data-stale={r.stale ? "1" : undefined}><button type="button" className="open mono" onClick={() => onOpen(project, r.profile, r.key)}>{r.key}{r.stale && <span className="stale" title={t("secrets.stale")} />}</button>{canWrite && <SecretActions confirming={confirm === ref} onChange={() => onChange(project, r.profile, r.key)} onRemove={() => confirm === ref ? void remove(r.profile, r.key) : ask(ref)} />}</li>;
          })}</List></section>;
      $[23] = ask;
      $[24] = canWrite;
      $[25] = confirm;
      $[26] = head;
      $[27] = onChange;
      $[28] = onOpen;
      $[29] = project;
      $[30] = remove;
      $[31] = t;
      $[32] = t6;
    } else {
      t6 = $[32];
    }
    let t7;
    if ($[33] !== data.error || $[34] !== data.reload || $[35] !== error || $[36] !== t) {
      t7 = (error || data.error) && <p className="note"><ErrorLine inline={true}>{error ?? data.error}</ErrorLine>{" "}<LinkButton underline={true} onClick={data.reload}>{t("secrets.retry")}</LinkButton></p>;
      $[33] = data.error;
      $[34] = data.reload;
      $[35] = error;
      $[36] = t;
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    t3 = <section data-t="secret-project" data-id={project}>{t5}{gs.map(t6)}{data.data && gs.length === 0 && <>{name && <SectionHead>{name}</SectionHead>}<p className="note">{t("secrets.none")}</p></>}{t7}{recipients && data.data && !data.data.remote && <Recipients project={project} data={data.data} stale={gs.some(_temp4)} />}</section>;
    $[3] = ask;
    $[4] = canWrite;
    $[5] = confirm;
    $[6] = data.data;
    $[7] = data.error;
    $[8] = data.loading;
    $[9] = data.reload;
    $[10] = error;
    $[11] = name;
    $[12] = onChange;
    $[13] = onOpen;
    $[14] = project;
    $[15] = recipients;
    $[16] = remove;
    $[17] = t;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
}

/** Who can decrypt: each key, `pending` while some value is not sealed to it yet; a reseal from this device when it
 *  holds a key; my devices revoked alone (a second tap). */
function _temp4(g_0) {
  return g_0.rows.some(_temp3);
}
function _temp3(r_0) {
  return r_0.stale;
}
function Recipients(t0) {
  const $ = _c(42);
  const {
    project,
    data,
    stale
  } = t0;
  const {
    t
  } = useTranslation();
  const person = useSession()?.person.id ?? "";
  const {
    ask,
    field
  } = usePassphrase();
  const task = useTask();
  const [confirm, setConfirm] = useState(null);
  let t1;
  if ($[0] !== data.secrets) {
    t1 = key => data.secrets.some(s => s.status === "active" && !s.recipients.includes(key));
    $[0] = data.secrets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pending = t1;
  let t2;
  let t3;
  let t4;
  if ($[2] !== ask || $[3] !== confirm || $[4] !== data.recipients || $[5] !== pending || $[6] !== person || $[7] !== project || $[8] !== stale || $[9] !== t || $[10] !== task) {
    const mine = data.recipients.filter(_temp5);
    let t5;
    if ($[14] !== person || $[15] !== task) {
      t5 = id => task.run(async () => {
        await api.secrets.revokeDevice(id);
        if (local(person)?.id === id) {
          forget(person);
        }
        changed(id);
      });
      $[14] = person;
      $[15] = task;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    const revoke = t5;
    let t6;
    if ($[17] !== t) {
      t6 = t("secrets.recipients");
      $[17] = t;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    if ($[19] !== t6) {
      t2 = <SectionHead>{t6}</SectionHead>;
      $[19] = t6;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    let t7;
    if ($[21] !== data.recipients || $[22] !== pending || $[23] !== t) {
      let t8;
      if ($[25] !== pending || $[26] !== t) {
        t8 = r_0 => <Item key={r_0.key} t="recipient" label={`${r_0.names.join(", ") || t("secrets.unknownOwner")}${pending(r_0.key) ? ` \u00b7 ${t("secrets.pending")}` : ""}`} value={shortKey(r_0.key)} mono={true} />;
        $[25] = pending;
        $[26] = t;
        $[27] = t8;
      } else {
        t8 = $[27];
      }
      t7 = data.recipients.map(t8);
      $[21] = data.recipients;
      $[22] = pending;
      $[23] = t;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    if ($[28] !== t7) {
      t3 = <List t="secret-recipients">{t7}</List>;
      $[28] = t7;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    t4 = stale && local(person) || mine.length ? <List>{stale && local(person) && <li><Row t="secret-reseal" label={task.busy ? t("secrets.resealing") : t("secrets.reseal")} disabled={task.busy} onClick={() => void task.run(async () => (await reseal(person, project, ask), changed(project)))} /></li>}{mine.map(r_1 => <li key={r_1.key}><Row t="device-revoke" danger={true} label={confirm === r_1.device ? t("secrets.confirmRevoke") : t("secrets.revoke", {
          name: r_1.names[0]
        })} onClick={() => confirm === r_1.device ? void revoke(r_1.device) : setConfirm(r_1.device)} /></li>)}</List> : null;
    $[2] = ask;
    $[3] = confirm;
    $[4] = data.recipients;
    $[5] = pending;
    $[6] = person;
    $[7] = project;
    $[8] = stale;
    $[9] = t;
    $[10] = task;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
  }
  let t5;
  if ($[30] !== task.error) {
    t5 = task.error && <ErrorLine>{task.error}</ErrorLine>;
    $[30] = task.error;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  let t6;
  if ($[32] !== stale || $[33] !== t) {
    t6 = stale && <p className="note">{t("secrets.pendingNote")}</p>;
    $[32] = stale;
    $[33] = t;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== field || $[36] !== t2 || $[37] !== t3 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6) {
    t7 = <>{t2}{t3}{t4}{field}{t5}{t6}</>;
    $[35] = field;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
  } else {
    t7 = $[41];
  }
  return t7;
}

/** A value on screen after the passkey, for SHOWN_MS or until the page is hidden; gone from state after. */
function _temp5(r) {
  return r.device;
}
function Reveal(t0) {
  const $ = _c(21);
  const {
    project,
    r
  } = t0;
  const {
    t
  } = useTranslation();
  const person = useSession()?.person.id ?? "";
  const {
    ask,
    field
  } = usePassphrase();
  const task = useTask();
  const [value, setValue] = useState(null);
  let t1;
  let t2;
  if ($[0] !== value) {
    t1 = () => {
      if (value === null) {
        return;
      }
      const hide = () => setValue(null);
      const timer = setTimeout(hide, SHOWN_MS);
      document.addEventListener("visibilitychange", hide);
      return () => {
        clearTimeout(timer);
        document.removeEventListener("visibilitychange", hide);
      };
    };
    t2 = [value];
    $[0] = value;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!local(person)) {
    let t3;
    if ($[3] !== t) {
      t3 = t("secrets.noDeviceKey");
      $[3] = t;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t3) {
      t4 = <p className="note">{t3}</p>;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  let t3;
  if ($[7] !== ask || $[8] !== person || $[9] !== project || $[10] !== r || $[11] !== t || $[12] !== task || $[13] !== value) {
    t3 = <List t="secret-reveal">{value === null ? <li><Row t="secret-show" label={task.busy ? t("secrets.revealing") : t("secrets.reveal")} disabled={task.busy} onClick={() => void task.run(async () => setValue(await reveal(person, project, r, ask)))} /></li> : <><Item t="secret-value" label={t("secrets.valueLabel")} value={value} mono={true} /><li><Row t="secret-copy" label={t("secrets.copy")} onClick={() => void navigator.clipboard.writeText(value)} /></li><li><Row t="secret-hide" label={t("secrets.hide")} onClick={() => setValue(null)} /></li></>}</List>;
    $[7] = ask;
    $[8] = person;
    $[9] = project;
    $[10] = r;
    $[11] = t;
    $[12] = task;
    $[13] = value;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== task.error) {
    t4 = task.error && <ErrorLine>{task.error}</ErrorLine>;
    $[15] = task.error;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== field || $[18] !== t3 || $[19] !== t4) {
    t5 = <>{t3}{field}{t4}</>;
    $[17] = field;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}

/** Remove with a second tap to confirm (the bin turns red). */
function useRemove(project, after) {
  const $ = _c(8);
  const [confirm, setConfirm] = useState(null);
  const {
    error,
    run
  } = useTask();
  let t0;
  if ($[0] !== after || $[1] !== project || $[2] !== run) {
    t0 = (profile, key) => run(async () => {
      await api.secrets.remove(project, profile, key);
      setConfirm(null);
      changed(`${profile}/${key}`);
      after?.();
    });
    $[0] = after;
    $[1] = project;
    $[2] = run;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const remove = t0;
  let t1;
  if ($[4] !== confirm || $[5] !== error || $[6] !== remove) {
    t1 = {
      confirm,
      ask: setConfirm,
      remove,
      error
    };
    $[4] = confirm;
    $[5] = error;
    $[6] = remove;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function SecretActions(t0) {
  const $ = _c(17);
  const {
    confirming,
    onChange,
    onRemove
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("secrets.change");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon name="pencil" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onChange || $[4] !== t1) {
    t3 = <IconButton t="secret-change" label={t1} onClick={onChange}>{t2}</IconButton>;
    $[3] = onChange;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== confirming || $[7] !== t) {
    t4 = confirming ? t("secrets.confirmRemove") : t("secrets.remove");
    $[6] = confirming;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = confirming ? "danger" : undefined;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon name="trash" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onRemove || $[11] !== t4 || $[12] !== t5) {
    t7 = <IconButton t="secret-remove" label={t4} className={t5} onClick={onRemove}>{t6}</IconButton>;
    $[10] = onRemove;
    $[11] = t4;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t7) {
    t8 = <span className="actions">{t3}{t7}</span>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

/** The details panel (?sec=<project>|<profile>|<key>): everything the row leaves out, and the same two actions. */
export function SecretDetail(t0) {
  const $ = _c(39);
  const {
    value,
    onChange,
    onClose
  } = t0;
  registerSecretsStrings();
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== value) {
    t1 = value.split("|");
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2, t3, t4] = t1;
  const project = t2 === undefined ? "" : t2;
  const profile = t3 === undefined ? "" : t3;
  const key = t4 === undefined ? "" : t4;
  const {
    projects,
    session
  } = useSecretProjects();
  let t5;
  if ($[2] !== project) {
    t5 = () => api.secrets.list(project);
    $[2] = project;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      kinds: KINDS
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const data = useLoad(t5, `secrets:${project}`, t6);
  const {
    confirm,
    ask,
    remove,
    error
  } = useRemove(project, onClose);
  let row;
  let t7;
  let t8;
  let t9;
  if ($[5] !== ask || $[6] !== confirm || $[7] !== data.data || $[8] !== data.loading || $[9] !== key || $[10] !== onChange || $[11] !== profile || $[12] !== project || $[13] !== projects || $[14] !== remove || $[15] !== session || $[16] !== t) {
    row = data.data ? groups(data.data).flatMap(_temp6).find(r => r.profile === profile && r.key === key) : undefined;
    let t10;
    if ($[21] !== project || $[22] !== session) {
      t10 = canWriteSecrets(session, project);
      $[21] = project;
      $[22] = session;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    const canWrite = t10;
    const ref = `${profile}/${key}`;
    t7 = "secret-detail";
    if ($[24] !== data.loading) {
      t8 = <Loader on={data.loading} />;
      $[24] = data.loading;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    t9 = row && <><List><Item label={t("secrets.project")} value={projects.find(p => p.id === project)?.name ?? project} /><Item label={t("secrets.profileLabel")} value={profile} mono={true} /><Item label={t("secrets.revLabel")} value={row.rev ?? 0} mono={true} /><Item label={t("secrets.changedLabel")} value={row.changedAt ? date(row.changedAt) : "—"} /><Item label={t("secrets.readsLabel")} value={row.reads ? t("secrets.reads", {
          n: row.reads
        }) : t("secrets.never")} />{row.lastReadAt && <Item label={t("secrets.lastReadLabel")} value={`${date(row.lastReadAt)} \u00b7 ${row.lastReadBy?.name ?? "?"}`} />}</List>{row.stale && <ErrorLine>{t("secrets.stale")}</ErrorLine>}{!projects.find(p_0 => p_0.id === project)?.instance && <Reveal project={project} r={ref} />}{canWrite && <List><li><Row t="secret-change" label={t("secrets.change")} chevron={true} onClick={() => onChange(project, profile, key)} /></li><li><Row t="secret-remove" danger={true} label={confirm === ref ? t("secrets.confirmRemove") : t("secrets.remove")} onClick={() => confirm === ref ? void remove(profile, key) : ask(ref)} /></li></List>}</>;
    $[5] = ask;
    $[6] = confirm;
    $[7] = data.data;
    $[8] = data.loading;
    $[9] = key;
    $[10] = onChange;
    $[11] = profile;
    $[12] = project;
    $[13] = projects;
    $[14] = remove;
    $[15] = session;
    $[16] = t;
    $[17] = row;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    row = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t10;
  if ($[26] !== data.data || $[27] !== row || $[28] !== t) {
    t10 = data.data && !row && <p className="note">{t("secrets.gone")}</p>;
    $[26] = data.data;
    $[27] = row;
    $[28] = t;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== data.error || $[31] !== error) {
    t11 = (error || data.error) && <ErrorLine>{error ?? data.error}</ErrorLine>;
    $[30] = data.error;
    $[31] = error;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t12 = <div data-t={t7}>{t8}{t9}{t10}{t11}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
}

/** The Add panel. `value`: "new", a project id (the target), or `<project>|<profile>|<key>` (change that one). */
function _temp6(g) {
  return g.rows;
}
export function AddSecret({
  value,
  scope,
  onClose
}: {
  value: string;
  scope: string | null;
  onClose: () => void;
}) {
  registerSecretsStrings();
  const {
    t
  } = useTranslation();
  const {
    projects
  } = useSecretProjects(true);
  const [p0, profile0, key0] = value === "new" ? [] : value.split("|");
  const editing = !!(profile0 && key0);
  const [target, setTarget] = useState<string | null>(p0 ?? null);
  const project = projects.find(p => p.id === (target ?? scope))?.id ?? projects[0]?.id ?? null;
  const [key, setKey] = useState(key0 ?? "");
  const [profile, setProfile] = useState(profile0 ?? "");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recipients = useLoad(() => project ? api.secrets.list(project) : Promise.resolve(null), `secrets:${project ?? ""}`, {
    kinds: KINDS
  });
  const known = (k: string) => k === "key" ? t("secrets.badKey") : k === "profile" ? t("secrets.badProfile") : k;
  const save = async () => {
    if (!project) return;
    const pr = profile.trim() || "default";
    const bad = invalid(key.trim(), pr);
    if (bad) return setError(bad);
    setSaving(true);
    setError(null);
    try {
      await api.secrets.save(project, pr, key.trim(), secret);
      setSecret(""); // the value is dropped at once: nothing keeps it after the request
      changed(`${pr}/${key.trim()}`);
      onClose();
    } catch (e) {
      setSecret("");
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  if (!projects.length) return <p className="note">{t("secrets.readOnly")}</p>;
  return <section data-t="secret-form">
			<SectionHead>{t("secrets.project")}</SectionHead>
			<Segmented t="secret-projects" variant="pills" label={t("secrets.projects")}>
				{(editing ? projects.filter(p_0 => p_0.id === project) : projects).map(p_1 => <Segment key={p_1.id} pressed={p_1.id === project} onClick={() => setTarget(p_1.id)} data-id={p_1.id}>
						{p_1.name}
					</Segment>)}
			</Segmented>
			<SectionHead>{editing ? t("secrets.changing", {
        ref: `${profile0}/${key0}`
      }) : t("secrets.add")}</SectionHead>
			{!editing && <>
					<Field t="secret-key" placeholder={t("secrets.name")} aria-label={t("secrets.name")} autoCapitalize="characters" autoCorrect="off" spellCheck={false} value={key} onChange={e_0 => setKey(e_0.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))} />
					<Field t="secret-profile" placeholder={t("secrets.profile")} aria-label={t("secrets.profile")} autoCapitalize="none" autoCorrect="off" spellCheck={false} value={profile} onChange={e_1 => setProfile(e_1.target.value.toLowerCase())} />
				</>}
			<Field t="secret-value" type="password" autoComplete="off" placeholder={t("secrets.value")} aria-label={t("secrets.value")} value={secret} onChange={e_2 => setSecret(e_2.target.value)} onKeyDown={e_3 => e_3.key === "Enter" && secret && key && void save()} action={<LinkButton data-t="secret-save" disabled={!secret || !key || saving || !project} onClick={() => void save()}>
						{saving ? t("secrets.saving") : t("secrets.save")}
					</LinkButton>} />
			{error && <ErrorLine>{known(error)}</ErrorLine>}
			{recipients.data && <p className="note">{t("secrets.encryptNote", {
        n: recipients.data.recipients.length
      })}</p>}
		</section>;
}